/**
 * The frozen 6,236-row word-count fixture, loaded. SPEC §3.2.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS FILE READS A FROZEN CONTRACT. See the banner in `types.ts`.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * "Canonical word segmentation is a property of the anchor space, frozen behind
 * the committed 6,236-row word-count fixture and versioned as
 * `anchor_space_version`." — SPEC §3.2.
 *
 * The fixture is generated by `scripts/build-anchor-fixture.ts` from the
 * SHA-pinned Tanzil source and COMMITTED. It is not a cache and it is not
 * rebuilt at runtime: the degradation ladder's tier-3 test is "word count for
 * the ayah changed", and a count regenerated from whatever text is currently
 * installed would compare that text to itself and could never fire.
 *
 * "A renderer that cannot supply a wordmap to the canonical segmentation may not
 * render word anchors; it degrades to ayah level with a visible note." —
 * SPEC §3.2. `canRenderWordAnchors` is that check.
 */

import fixture from './word-counts.json';
import { AYAH_OFFSET, SURAH_AYAH_COUNT, ayahIdOf, isValidAyahId, isValidLocation } from './offsets';
import { ANCHOR_SPACE_VERSION, SURAH_COUNT, TOTAL_AYAT, WORD_SLOTS, type AyahId } from './types';

/** The `_notice` the Tanzil licence requires every derived file to carry. */
export const FIXTURE_NOTICE: string = fixture._notice;

/** sha256 of the canonical `counts[1..6236].join(' ')` string. */
export const FIXTURE_COUNTS_SHA256: string = fixture.counts_sha256;

/** The `anchor_space_version` the fixture was generated under. */
export const FIXTURE_ANCHOR_SPACE_VERSION: string = fixture.anchor_space_version;

/** sha256 of the vendored Tanzil source the fixture was derived from. */
export const FIXTURE_SOURCE_SHA256: string = fixture.source.sha256;

/** Total canonical words in the mushaf under this anchor space. */
export const TOTAL_WORDS: number = fixture.total_words;

/** The longest ayah, in canonical words. Must stay below `WORD_SLOTS`. */
export const MAX_WORDS_IN_AYAH: number = fixture.max_words_in_ayah;

/** `surah:ayah` of the longest ayah. 2:282 under `seg-1`. */
export const MAX_WORDS_AT: string = fixture.max_words_at;

/* ───────────────────────── LAZY, AND WHY ──────────────────────────────────
 *
 * `WORD_COUNTS` is built on FIRST USE, not at module evaluation.
 *
 * Nearly every module in the app will transitively import `$lib/anchor`. If the
 * table were built at module scope and the fixture were malformed, every one of
 * those modules would fail to EVALUATE — including `/repair`, the tier-4 screen
 * that exists precisely to recover from a broken anchor space. In an
 * offline-first PWA that is a white screen with no in-app route out of it, and
 * the user's only remaining move is to clear site data, which destroys
 * everything. A frozen contract may not have that failure mode.
 *
 * So the structural validation lives in the BUILD — `scripts/build-anchor-
 * fixture.ts` runs `parseWordCounts` over exactly the bytes it is about to
 * commit, and CI runs it again with `--check` — and the runtime path is total:
 * importing this module always succeeds, and only a call that actually needs a
 * word count can fail.
 * ────────────────────────────────────────────────────────────────────────── */

/** Memoised result of the first `parseWordCounts` call. */
let CACHED: Int16Array | null = null;
/** Memoised failure, so a broken fixture is not re-parsed on every read. */
let CACHED_ERROR: Error | null = null;

/**
 * `WORD_COUNTS[ayahId]` — index 0 is padding, matching the offset tables.
 * `Int16Array` because the values are 1..128 and a typed array of 6,237 entries
 * is 12 KB of contiguous memory with no per-element boxing; this is read on
 * every page turn.
 */
function wordCountTable(): Int16Array {
	if (CACHED) return CACHED;
	if (CACHED_ERROR) throw CACHED_ERROR;
	try {
		CACHED = parseWordCounts(fixture.counts as unknown as Record<string, string>);
		return CACHED;
	} catch (error) {
		CACHED_ERROR = error instanceof Error ? error : new Error(String(error));
		throw CACHED_ERROR;
	}
}

/**
 * `true` iff the fixture can be read at all. The read path calls this before it
 * renders word anchors, and `/repair` calls it to explain why it cannot.
 * Never throws.
 */
export function wordmapIsUsable(): boolean {
	try {
		wordCountTable();
		return true;
	} catch {
		return false;
	}
}

/**
 * The structural validation of `word-counts.json`, as a pure function of the
 * `counts` object. Exported so `scripts/build-anchor-fixture.ts` can run it
 * over the bytes it is about to commit — the build is where a malformed
 * fixture must be caught, because at runtime there is nowhere to report it to.
 */
export function parseWordCounts(rows: Record<string, string>): Int16Array {
	// The JSON's `counts` is one space-separated row per surah — see the note in
	// the generator about why it is not a flat 6,236-element array.
	const out = new Int16Array(TOTAL_AYAT + 1);

	for (let surah = 1; surah <= SURAH_COUNT; surah++) {
		const row = rows[String(surah)];
		if (row === undefined) {
			throw new Error(`word-counts.json: surah ${surah} is missing`);
		}
		const fields = row.split(' ');
		const expected = SURAH_AYAH_COUNT[surah] as number;
		if (fields.length !== expected) {
			throw new Error(
				`word-counts.json: surah ${surah} has ${fields.length} counts, expected ${expected}`
			);
		}
		const base = AYAH_OFFSET[surah] as number;
		for (let i = 0; i < fields.length; i++) {
			const n = Number(fields[i]);
			if (!Number.isInteger(n) || n < 1 || n >= WORD_SLOTS) {
				throw new Error(`word-counts.json: ${surah}:${i + 1} has an impossible count ${fields[i]}`);
			}
			out[base + i + 1] = n;
		}
	}
	return out;
}

/** Canonical word count of an ayah under the frozen segmentation. */
export function wordCountOf(ayahId: AyahId): number {
	if (!isValidAyahId(ayahId)) {
		throw new RangeError(`ayahId ${ayahId} is not in 1..${TOTAL_AYAT}`);
	}
	return wordCountTable()[ayahId] as number;
}

/** Read-only view of the whole table, for tests and the integrity gate. */
export function wordCounts(): Readonly<Int16Array> {
	return wordCountTable();
}

/** `true` iff `wordIndex` names a word that exists in `ayahId` today. */
export function isWordInAyah(ayahId: AyahId, wordIndex: number): boolean {
	if (!isValidAyahId(ayahId)) return false;
	if (!Number.isInteger(wordIndex) || wordIndex < 0) return false;
	return wordIndex < (wordCountTable()[ayahId] as number);
}

/**
 * `true` iff `[start, end)` names words that ALL EXIST in `surah:ayah` under
 * the frozen fixture. `end` is EXCLUSIVE, matching the `ref` grammar.
 *
 * THIS IS THE CHECK NO LAYER HAD. `parseRef` bounds a word span against
 * `WORD_SLOTS` (1024) — which it must, because a ref minted under a NEWER
 * anchor space has to quarantine rather than be destroyed — and nothing
 * anywhere compared a span against the ayah's ACTUAL count. The app could
 * therefore mint a durable, exportable, never-flagged ref addressing words that
 * do not exist: `wordsRef(112, 1, 0, 900)` into an ayah of four words.
 *
 * MINT TIME is where this belongs and the only place it belongs:
 *   - `wordsRef()` calls it as a HARD REJECT. A coordinate this app creates
 *     must name a place that exists.
 *   - `parseRef()` does NOT. A ref that arrives from a future anchor space is
 *     quarantined (tier 4) and shown on the Repair screen with its quote —
 *     never deleted, never silently rewritten.
 *   - the import path calls `refIsInRange()` (codec.ts) to decide drift and
 *     quarantine.
 */
export function isWordSpanInAyah(surah: number, ayah: number, start: number, end: number): boolean {
	if (!isValidLocation(surah, ayah)) return false;
	if (!Number.isInteger(start) || !Number.isInteger(end)) return false;
	if (start < 0 || end <= start) return false;
	return end <= (wordCountTable()[ayahIdOf(surah, ayah)] as number);
}

/**
 * SPEC §3.2's renderer gate. A surface that cannot map the text it is showing
 * onto the canonical segmentation must not draw word anchors at all; it shows
 * the whole ayah and says so. Comparing the version the anchor was minted under
 * against the version the fixture was built under is that check.
 */
export function canRenderWordAnchors(anchorSpaceVersion: string | undefined): boolean {
	if (!anchorSpaceVersion) return false;
	return anchorSpaceVersion === FIXTURE_ANCHOR_SPACE_VERSION;
}

/**
 * `true` iff the fixture on disk agrees with the constants compiled into the
 * app. A mismatch means someone edited one without the other, and every
 * `word_start` in the database is now addressing a segmentation that no code
 * path produces.
 */
export function fixtureIsConsistent(): boolean {
	return (
		fixture.total_ayat === TOTAL_AYAT &&
		fixture.word_slots === WORD_SLOTS &&
		fixture.anchor_space_version === ANCHOR_SPACE_VERSION &&
		fixture.max_words_in_ayah < WORD_SLOTS
	);
}
